import React, { memo, useCallback, useEffect, useRef, useState } from 'react';

import { ChevronDownIcon } from 'lucide-react';
import {
  Button,
  Card,
  Command,
  CommandGroup,
  CommandItem,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Input,
  LoadingSpinner,
  Popover,
  PopoverContent,
  PopoverTrigger,
  ScrollArea,
  ScrollBar,
  Table,
  TableBody,
  TableCell,
  TableDock,
  TableHead,
  TableHeader,
  TableRow,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@ais-ui/ui-core';
import { cn, formatVesselName } from '@ais-ui/utils';
import { usePagination, useTargetsOnMap } from '../TargetsOnMapContext';
import { useLocalization } from '@ais-ui/locale-management';
import { useMapPanelContext } from '../MapPanel/MapPanelContext';
import {
  PiBoat,
  PiCaretDown,
  PiCaretUp,
  PiCaretUpDown,
  PiDotsThreeVerticalBold,
  PiFileCsv,
  PiPath,
} from 'react-icons/pi';
import { useShipSelection } from '../contexts/ShipSelectionContext';
import { AutoCompleteShipInfo, TargetFromTargetList } from '@ais-ui/websockets';
import { CENTERCOMPONENTS } from '../MapPanel/CenterComponents';
import {
  useTableDensity,
  TruncatedCell,
  ShortTextCell,
} from '../../utils/useTableDensity';
import {
  getDimensionsFromMap,
  useOpenLayersContext,
} from '@ais-ui/react-openlayers';
import { openPopupOnSecondaryScreen, PopupType } from '../../Popup';
import { useShowAllTracks } from './useShowAllTracks';
import { BULK_TRACKS_MAX } from '../TargetsOnMapContext';

interface TargetsOnMapTableProps {
  className?: string;
}

interface SortableTableHeadProps {
  children: React.ReactNode;
  column: string;
  density: 'normal' | 'compact';
  densityClasses: any;
}

const SortableTableHead = ({
  children,
  column,
  density,
  densityClasses,
}: SortableTableHeadProps) => {
  const {
    orderBy,
    setOrderBy,
    orderAscending,
    setOrderAscending,
    onGetTargets,
  } = useTargetsOnMap();
  const [sortAction, setSortAction] = useState(false);

  const isSorted = orderBy === column;

  const handleSort = useCallback(() => {
    if (orderBy === column) {
      setOrderAscending(!orderAscending);
    } else {
      setOrderAscending(true);
    }
    setOrderBy(column);

    setSortAction(true);
  }, [orderBy, column, setOrderAscending, orderAscending, setOrderBy]);

  useEffect(() => {
    if (sortAction) {
      onGetTargets();
      setSortAction(false);
    }
  }, [sortAction, onGetTargets]);

  return (
    <TableHead
      className={cn('cursor-pointer select-none', densityClasses.tableHeader)}
      onClick={handleSort}
    >
      <div className="flex items-center justify-between">
        <span className={densityClasses.text}>{children}</span>
        <span className="mr-2">
          {isSorted ? (
            orderAscending ? (
              <PiCaretUp className={densityClasses.icon.small} />
            ) : (
              <PiCaretDown className={densityClasses.icon.small} />
            )
          ) : (
            <PiCaretUpDown
              className={cn(densityClasses.icon.small, 'opacity-50')}
            />
          )}
        </span>
      </div>
    </TableHead>
  );
};

const TargetsOnMapActionDropDown = () => {
  const { onCreateShiplist, isSubmitting, exportTargetsToCSV, total } =
    useTargetsOnMap();
  const { tableDensity, densityClasses } = useTableDensity();
  const [shiplistName, setShiplistName] = useState('');
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const { messages } = useLocalization();
  const {
    toggleBulkTracks,
    isBulkTracksActive,
    isSelectingBulkTracks,
    canActivate,
    targetCount,
  } = useShowAllTracks();

  const isBulkTracksDisabled =
    isSubmitting ||
    isSelectingBulkTracks ||
    (!isBulkTracksActive && !canActivate);

  const bulkTracksDisabledReason =
    !isBulkTracksActive && !isSelectingBulkTracks && total > BULK_TRACKS_MAX
      ? `Showing all tracks is limited to ${BULK_TRACKS_MAX} targets. There are currently ${total} targets on the map — narrow the list to ${BULK_TRACKS_MAX} or fewer to enable this.`
      : !isBulkTracksActive && !isSelectingBulkTracks && targetCount === 0
        ? 'There are no targets on the map to show tracks for.'
        : undefined;

  const bulkTracksLabel = isSelectingBulkTracks
    ? `Selecting tracks...`
    : isBulkTracksActive
      ? 'Hide all tracks'
      : total > BULK_TRACKS_MAX
        ? `Show all tracks (max ${BULK_TRACKS_MAX})`
        : `Show all tracks (${targetCount})`;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="m-0 h-[26px] gap-1 px-2.5 text-xs font-medium"
        >
          Actions{' '}
          <ChevronDownIcon className={cn('ml-1', densityClasses.icon.small)} />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72">
        <Command>
          <CommandGroup heading="Actions">
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="block">
                  <CommandItem
                    onSelect={() => toggleBulkTracks()}
                    disabled={isBulkTracksDisabled}
                  >
                    <PiPath className={cn('mr-2', densityClasses.icon.small)} />
                    {bulkTracksLabel}
                  </CommandItem>
                </span>
              </TooltipTrigger>
              {bulkTracksDisabledReason && (
                <TooltipContent side="right" className="max-w-64">
                  {bulkTracksDisabledReason}
                </TooltipContent>
              )}
            </Tooltip>
            <CommandItem onSelect={exportTargetsToCSV} disabled={isSubmitting}>
              <PiFileCsv className={cn('mr-2', densityClasses.icon.small)} />
              {messages.targetsOnMapExportCsvText}
            </CommandItem>
            <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
              <PopoverTrigger asChild>
                <CommandItem onSelect={() => setIsPopoverOpen(true)}>
                  <PiBoat className={cn('mr-2', densityClasses.icon.small)} />
                  {messages.targetsOnMapCreateShiplist}
                </CommandItem>
              </PopoverTrigger>
              <PopoverContent side={'right'}>
                <Input
                  placeholder="name"
                  value={shiplistName}
                  onChange={(e) => setShiplistName(e.target.value)}
                  className={cn(tableDensity === 'compact' && 'h-8 text-xs')}
                ></Input>
                <div className="w-full flex justify-end">
                  <Button
                    onClick={() => onCreateShiplist(shiplistName)}
                    className={cn(
                      'mt-2',
                      tableDensity === 'compact' && 'h-8 px-3 text-xs',
                    )}
                    disabled={isSubmitting}
                  >
                    {isSubmitting
                      ? messages.genericSubmitting
                      : messages.genericCreate}
                  </Button>
                </div>
              </PopoverContent>
            </Popover>
          </CommandGroup>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export const TargetsOnMapTable = memo(
  ({ className }: TargetsOnMapTableProps) => {
    const { tableExpanded, setTableExpanded, tableHeight, setCenterComponent } =
      useMapPanelContext();
    const { messages } = useLocalization();
    const { tableDensity, densityClasses } = useTableDensity();

    const viewportRef = useRef<HTMLDivElement | null>(null);

    const { setZoomToShip } = useShipSelection();
    const { map, displayProjection, dataProjection } = useOpenLayersContext();

    const {
      onGetTargets,
      targets,
      page,
      setPage,
      perPage,
      total,
      isLoading,
      retrievalTime,
    } = useTargetsOnMap();
    const pagination = usePagination(page, total, perPage);

    const onPrevious = useCallback(() => {
      setPage((prev) => Math.max(prev - 1, 1));
    }, [setPage]);

    const onNext = useCallback(() => {
      setPage((prev) => Math.min(prev + 1, Math.ceil(total / perPage)));
    }, [setPage, total, perPage]);

    useEffect(() => {
      onGetTargets();
    }, [page, onGetTargets]);

    const onGoToTarget = useCallback(
      (target: TargetFromTargetList) => {
        // The backend sets the mmsi as the name in some cases, and if we try to search by mmsi name, it'll not be found
        const name = target.name !== target.mmsi ? target.name : '';
        const autoCompleteShip: AutoCompleteShipInfo = {
          name: name,
          mmsi:
            target.mmsi !== null && target.mmsi !== undefined
              ? String(target.mmsi)
              : null,
          imo:
            target.imo !== null && target.imo !== undefined
              ? String(target.imo)
              : null,
          callSign:
            target.callSign !== null && target.callSign !== undefined
              ? target.callSign
              : null,
          type: 'TT_SHIP',
        };
        setZoomToShip(autoCompleteShip);
      },
      [setZoomToShip],
    );

    const onClose = useCallback(() => {
      setCenterComponent(CENTERCOMPONENTS.NONE);
    }, [setCenterComponent]);

    const handleOpenInPopup = useCallback(() => {
      const dimensions = getDimensionsFromMap(
        map,
        displayProjection,
        dataProjection,
      );
      if (!dimensions) return;

      const { northEast, southWest } = dimensions;

      openPopupOnSecondaryScreen(PopupType.Targets, {
        minLat: southWest.lat,
        maxLat: northEast.lat,
        minLon: southWest.lng,
        maxLon: northEast.lng,
      });
    }, [map, displayProjection, dataProjection]);

    if (!tableExpanded) {
      return null;
    }

    return (
      <Card
        variant="elevated"
        className={cn(
          'relative flex flex-col h-fit w-full transition-all group/data-table pl-1 pr-3 pb-4 pt-1',
          className,
        )}
      >
        <TableDock>
          <TableDock.Identity
            icon={<PiBoat />}
            title={messages.targetsOnMapTableTitle}
          />
          <TableDock.State />
          <TableDock.Controls>
            <TargetsOnMapActionDropDown />
            <TableDock.Divider />
            <TableDock.Pager
              from={total === 0 ? 0 : (page - 1) * perPage + 1}
              to={Math.min(page * perPage, total)}
              total={total}
              onPrev={onPrevious}
              onNext={onNext}
              prevDisabled={page === 1}
              nextDisabled={page >= pagination.totalPages}
            />
            <TableDock.Divider />
            <TableDock.PopOut
              id="open-targets-popup-button"
              onClick={handleOpenInPopup}
            />
            <TableDock.Close onClick={onClose} />
          </TableDock.Controls>
        </TableDock>
        <div className="flex overflow-hidden">
          <ScrollArea
            infiniteScrollRef={viewportRef}
            observeResize={false}
            className="mb-4 mx-2 flex-grow p-0 overflow-auto border rounded-md max-w-full"
          >
            <Table container={false}>
              <TableHeader className="sticky top-0 left-0 right-0 bg-background no-border">
                <TableRow className="hover:bg-transparent">
                  <TableHead className={cn('w-4', densityClasses.tableHeader)}>
                    <span className={densityClasses.text}>#</span>
                  </TableHead>
                  <SortableTableHead
                    column="name"
                    density={tableDensity}
                    densityClasses={densityClasses}
                  >
                    {messages.targetsOnMapHeaderName}
                  </SortableTableHead>
                  <SortableTableHead
                    column="mmsi"
                    density={tableDensity}
                    densityClasses={densityClasses}
                  >
                    {messages.targetsOnMapHeaderMmsi}
                  </SortableTableHead>
                  <SortableTableHead
                    column="imo"
                    density={tableDensity}
                    densityClasses={densityClasses}
                  >
                    {messages.targetsOnMapHeaderImo}
                  </SortableTableHead>
                  <SortableTableHead
                    column="callsign"
                    density={tableDensity}
                    densityClasses={densityClasses}
                  >
                    {messages.targetsOnMapHeaderCallsign}
                  </SortableTableHead>
                  <SortableTableHead
                    column="shiptype"
                    density={tableDensity}
                    densityClasses={densityClasses}
                  >
                    {messages.targetsOnMapHeaderShipType}
                  </SortableTableHead>
                  <SortableTableHead
                    column="type"
                    density={tableDensity}
                    densityClasses={densityClasses}
                  >
                    {messages.targetsOnMapHeaderAisType}
                  </SortableTableHead>
                  <SortableTableHead
                    column="country"
                    density={tableDensity}
                    densityClasses={densityClasses}
                  >
                    {messages.targetsOnMapHeaderCountry}
                  </SortableTableHead>
                  <TableHead className={densityClasses.tableHeader}>
                    <span className={densityClasses.text}>
                      {messages.targetsOnMapHeaderPosition}
                    </span>
                  </TableHead>
                  <SortableTableHead
                    column="sog"
                    density={tableDensity}
                    densityClasses={densityClasses}
                  >
                    {messages.targetsOnMapHeaderSpeed}
                  </SortableTableHead>
                  <SortableTableHead
                    column="cog"
                    density={tableDensity}
                    densityClasses={densityClasses}
                  >
                    {messages.targetsOnMapHeaderCourse}
                  </SortableTableHead>
                  <SortableTableHead
                    column="heading"
                    density={tableDensity}
                    densityClasses={densityClasses}
                  >
                    {messages.targetsOnMapHeaderHeading}
                  </SortableTableHead>
                  <SortableTableHead
                    column="rot"
                    density={tableDensity}
                    densityClasses={densityClasses}
                  >
                    {messages.targetsOnMapHeaderRateOfTurn}
                  </SortableTableHead>
                  <SortableTableHead
                    column="width"
                    density={tableDensity}
                    densityClasses={densityClasses}
                  >
                    {messages.targetsOnMapHeaderWidth}
                  </SortableTableHead>
                  <SortableTableHead
                    column="length"
                    density={tableDensity}
                    densityClasses={densityClasses}
                  >
                    {messages.targetsOnMapHeaderLength}
                  </SortableTableHead>
                  <SortableTableHead
                    column="draught"
                    density={tableDensity}
                    densityClasses={densityClasses}
                  >
                    {messages.targetsOnMapHeaderDraught}
                  </SortableTableHead>
                  <TableHead className={densityClasses.tableHeader}></TableHead>
                </TableRow>
              </TableHeader>
              {isLoading && (
                <TableBody>
                  <TableRow className="border-0">
                    <TableCell colSpan={17}>
                      <div
                        className="flex justify-center items-center"
                        style={{ height: `${tableHeight - 170}px` }}
                      >
                        <LoadingSpinner />
                      </div>
                    </TableCell>
                  </TableRow>
                </TableBody>
              )}
              {!isLoading && (
                <TableBody>
                  {targets.map((target, index) => (
                    <TableRow
                      key={`${target.mmsi}-${target.name}`}
                      className="odd:bg-muted/50"
                    >
                      <TableCell className={densityClasses.tableCell}>
                        <div className={densityClasses.cellContent}>
                          {(page - 1) * perPage + index + 1}
                        </div>
                      </TableCell>
                      <TableCell
                        className={cn('font-medium', densityClasses.tableCell)}
                      >
                        <TruncatedCell
                          text={formatVesselName(target.name)}
                          className={cn(
                            densityClasses.cellContent,
                            densityClasses.truncation.medium,
                          )}
                        >
                          {formatVesselName(target.name)}
                        </TruncatedCell>
                      </TableCell>
                      <TableCell className={densityClasses.tableCell}>
                        <div className={densityClasses.cellContent}>
                          {target.mmsi}
                        </div>
                      </TableCell>
                      <TableCell className={densityClasses.tableCell}>
                        <div className={densityClasses.cellContent}>
                          {target.imo}
                        </div>
                      </TableCell>
                      <TableCell className={densityClasses.tableCell}>
                        <ShortTextCell className={densityClasses.cellContent}>
                          {target.callSign}
                        </ShortTextCell>
                      </TableCell>
                      <TableCell className={densityClasses.tableCell}>
                        <TruncatedCell
                          text={target.shipType || ''}
                          className={cn(
                            densityClasses.cellContent,
                            densityClasses.truncation.medium,
                          )}
                        >
                          {target.shipType}
                        </TruncatedCell>
                      </TableCell>
                      <TableCell className={densityClasses.tableCell}>
                        <ShortTextCell className={densityClasses.cellContent}>
                          {target.type}
                        </ShortTextCell>
                      </TableCell>
                      <TableCell className={densityClasses.tableCell}>
                        <TruncatedCell
                          text={target.country || ''}
                          className={cn(
                            densityClasses.cellContent,
                            densityClasses.truncation.medium,
                          )}
                        >
                          {target.country}
                        </TruncatedCell>
                      </TableCell>
                      <TableCell className={densityClasses.tableCell}>
                        <TruncatedCell
                          text={`${target.lat.toFixed(4)}, ${target.lon.toFixed(
                            4,
                          )}`}
                          className={cn(
                            densityClasses.cellContent,
                            tableDensity === 'compact'
                              ? 'max-w-32'
                              : 'max-w-40',
                          )}
                        >
                          {`${target.lat.toFixed(4)}, ${target.lon.toFixed(4)}`}
                        </TruncatedCell>
                      </TableCell>
                      <TableCell className={densityClasses.tableCell}>
                        <ShortTextCell className={densityClasses.cellContent}>
                          {target.sog && `${target.sog} knots`}
                        </ShortTextCell>
                      </TableCell>
                      <TableCell className={densityClasses.tableCell}>
                        <ShortTextCell className={densityClasses.cellContent}>
                          {target.cog && `${target.cog} °`}
                        </ShortTextCell>
                      </TableCell>
                      <TableCell className={densityClasses.tableCell}>
                        <ShortTextCell className={densityClasses.cellContent}>
                          {target.heading && `${target.heading} °`}
                        </ShortTextCell>
                      </TableCell>
                      <TableCell className={densityClasses.tableCell}>
                        <ShortTextCell className={densityClasses.cellContent}>
                          {target.rot && `${target.rot} °/min`}
                        </ShortTextCell>
                      </TableCell>
                      <TableCell className={densityClasses.tableCell}>
                        <ShortTextCell className={densityClasses.cellContent}>
                          {target.width && `${target.width} meters`}
                        </ShortTextCell>
                      </TableCell>
                      <TableCell className={densityClasses.tableCell}>
                        <ShortTextCell className={densityClasses.cellContent}>
                          {target.length && `${target.length} meters`}
                        </ShortTextCell>
                      </TableCell>
                      <TableCell className={densityClasses.tableCell}>
                        <ShortTextCell className={densityClasses.cellContent}>
                          {target.draught && `${target.draught} meters`}
                        </ShortTextCell>
                      </TableCell>
                      <TableCell className={densityClasses.tableCell}>
                        <div className={densityClasses.cellContent}>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                className={densityClasses.button}
                              >
                                <PiDotsThreeVerticalBold
                                  className={densityClasses.icon.medium}
                                />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onClick={() => onGoToTarget(target)}
                              >
                                View target
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              )}
            </Table>
            <ScrollBar orientation="horizontal" />
          </ScrollArea>
        </div>
      </Card>
    );
  },
);

TargetsOnMapTable.displayName = 'TargetsOnMapTable';
